import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { focusRing } from "../../design-system/focusRing";

// Picking one of a handful of named things — an integration, a provider — as the prototype
// does it: a grid of tiles showing each option's mark and name, the chosen one tinted rather
// than filled. A dropdown hides the options behind a click; a tile grid shows them, which is
// the point when there are nine and each has a logo the user recognises.
//
// Radio inputs underneath, so arrow keys move between options and the group announces itself
// as one choice rather than nine unrelated buttons.
const Grid = styled.div`
    /* PathwayFormGroup sets justify-items: start, which sizes its children to their content — so the
       grid took its intrinsic width and left the rest of the dialog empty. */
    width: 100%;
    display: grid;
    /* 230px, not 170px: three columns in an 800px dialog rather than four, as the prototype has them. At four
       the tile was ~190px and the longer integration names were truncated mid-word. */
    grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
    gap: var(--gap-m);
    /* A long list has to stay reachable inside a dialog rather than pushing its footer away. */
    max-height: 320px;
    overflow-y: auto;
    /* Room for the scrollbar, so the rightmost tiles are not cut by it. */
    padding-right: 2px;

    @media (max-width: 640px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const Tile = styled.label`
    ${focusRing}
    display: flex;
    /* Top-aligned, so a two-line label does not shift its mark off the first line. */
    align-items: flex-start;
    gap: var(--gap-l);
    min-width: 0;
    padding: 12px 14px;
    border: 1px solid ${(props) => (props.$selected ? "var(--pw-brand-cyan)" : "var(--border-hairline)")};
    border-radius: var(--pw-radius-lg);
    background: ${(props) => (props.$selected ? "var(--pw-tint-brand)" : "var(--surface-card)")};
    color: ${(props) => (props.$selected ? "var(--pw-brand-cyan)" : "var(--text-heading)")};
    font-size: var(--pw-text-control);
    font-weight: ${(props) => (props.$selected ? "var(--pw-weight-semibold)" : "var(--pw-weight-regular)")};
    cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
    opacity: ${(props) => (props.$disabled ? 0.55 : 1)};
    transition: border-color var(--pw-transition-fast) var(--pw-ease),
        background var(--pw-transition-fast) var(--pw-ease);

    &:hover {
        border-color: ${(props) => (props.$disabled ? "var(--border-hairline)" : "var(--pw-brand-cyan)")};
    }
`;

// The input drives selection and keyboard movement but is not drawn: the tile is the control
// the user sees.
const HiddenRadio = styled.input`
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
`;

const Mark = styled.img`
    margin-top: 1px;
    width: 20px;
    height: 20px;
    object-fit: contain;
    flex: none;
`;

const Glyph = styled.i`
    width: 20px;
    flex: none;
    font-size: var(--pw-text-md);
    text-align: center;
`;

// The prototype's options are one word each; ours run to "Stripe (via API-Schlüssel) - Super
// Admins only". Truncating those makes the tiles unreadable, so a label wraps to two lines and
// only then gives up.
const Name = styled.span`
    flex: 1;
    min-width: 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: var(--pw-leading-snug);
`;

const PathwayChoiceGrid = ({ name, options, value, onChange, label, dataCy }) => (
    <Grid role="radiogroup" aria-label={label} data-cy={dataCy}>
        {options.map((option) => {
            const selected = option.value === value;

            return (
                <Tile
                    key={option.value}
                    $selected={selected}
                    $disabled={option.disabled}
                    data-cy={dataCy ? `${dataCy}-${option.value}` : undefined}
                >
                    {/* The handler guards disabled as well as the attribute: a dispatched event
                        still reaches it even when the input is disabled. */}
                    <HiddenRadio
                        type="radio"
                        name={name}
                        value={option.value}
                        checked={selected}
                        disabled={option.disabled}
                        onChange={() => !option.disabled && onChange(option.value)}
                    />
                    {option.logo ? (
                        <Mark src={option.logo} alt="" />
                    ) : (
                        option.icon && <Glyph className={option.icon} aria-hidden="true" />
                    )}
                    <Name>{option.label}</Name>
                </Tile>
            );
        })}
    </Grid>
);

PathwayChoiceGrid.propTypes = {
    name: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.string.isRequired,
            label: PropTypes.node.isRequired,
            logo: PropTypes.string,
            icon: PropTypes.string,
            disabled: PropTypes.bool,
        })
    ).isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    label: PropTypes.string,
    dataCy: PropTypes.string,
};

export default PathwayChoiceGrid;
